// NOTIFY-001 / MAIL-001 admin client (P7-B). Shapes mirror packages/domain/notify/src/api/http.ts.
import { useQuery } from "@tanstack/react-query";
import { request } from "../api/client";

export type NotifyChannel = "inapp" | "push" | "email" | "sms";

export interface AppNotification {
  id: string;
  event: string;
  title_key: string;
  params: Record<string, string | number>;
  link: string | null;
  store_id: string | null;
  read: boolean;
  read_at: string | null;
  created_at: string;
}

export interface NotificationPage {
  data: AppNotification[];
  next_cursor: string | null;
  poll_interval_s: number;
}

export interface PrefsView {
  events: {
    event: string;
    channels: Record<NotifyChannel, boolean>;
    defaults: Record<NotifyChannel, boolean>;
  }[];
  digest: { frequency: "daily" | "weekly" };
  availability: Record<NotifyChannel, "available" | "activation_required">;
}

export interface PushConfig {
  state: "available" | "activation_required";
  vapid_public_key: string | null;
}

export interface RealtimeTicket {
  ticket: string;
  expires_at: string;
  channel: string;
  url: string;
  poll_interval_s: number;
}

export interface TemplateRow {
  event: string;
  channel: "sms" | "email";
  subject: string | null;
  body: string;
  custom: boolean;
}

export interface TemplatePreview {
  rendered: { subject: string | null; body: string };
  encoding?: "GSM-7" | "UCS-2";
  segments?: number;
  characters?: number;
  cost_paisa?: number | null;
  rate_available?: boolean;
  delivery: { state: "available" | "activation_required"; dependency?: string };
}

export interface SenderIdentity {
  id: string;
  kind: "email_domain" | "sms_sender_id";
  value: string;
  state: "pending" | "verified" | "failed";
}

export interface MailAlias {
  id: string;
  alias: string;
  forward_to: string;
  state: "pending" | "active" | "disabled";
  routing: string;
}

const o = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;
const s = (orgId: string, storeId: string) => `${o(orgId)}/stores/${encodeURIComponent(storeId)}`;

export const notificationsApi = {
  list: (orgId: string, q: { unread?: boolean; cursor?: string | null; limit?: number } = {}) =>
    request<NotificationPage>("GET", `${o(orgId)}/notifications`, {
      query: {
        ...(q.unread ? { unread: 1 } : {}),
        ...(q.cursor ? { cursor: q.cursor } : {}),
        ...(q.limit ? { limit: q.limit } : {}),
      },
    }),
  unreadCount: (orgId: string) =>
    request<{ unread: number; poll_interval_s: number }>(
      "GET",
      `${o(orgId)}/notifications/unread-count`,
    ),
  setRead: (orgId: string, id: string, read: boolean) =>
    request<AppNotification>("PATCH", `${o(orgId)}/notifications/${encodeURIComponent(id)}`, {
      body: { read },
    }),
  readAll: (orgId: string) =>
    request<{ updated: number }>("POST", `${o(orgId)}/notifications/read`, { body: { all: true } }),
  prefs: (orgId: string) => request<PrefsView>("GET", `${o(orgId)}/notification-prefs`),
  putPrefs: (
    orgId: string,
    body: {
      events: { event: string; channels: Partial<Record<NotifyChannel, boolean>> }[];
      digest?: { frequency: "daily" | "weekly" };
    },
  ) => request<PrefsView>("PUT", `${o(orgId)}/notification-prefs`, { body }),
  pushConfig: (orgId: string) => request<PushConfig>("GET", `${o(orgId)}/push/config`),
  subscribePush: (orgId: string, sub: PushSubscriptionJSON) =>
    request<{ id: string }>("POST", `${o(orgId)}/push-subscriptions`, {
      body: sub,
      idempotent: true,
    }),
  unsubscribePush: (orgId: string, endpoint: string) =>
    request<void>("DELETE", `${o(orgId)}/push-subscriptions`, { body: { endpoint } }),
  ticket: (orgId: string) =>
    request<RealtimeTicket>("POST", `${o(orgId)}/realtime/ticket`, { body: {} }),
  templates: (orgId: string, storeId: string) =>
    request<{ data: TemplateRow[]; variables: string[] }>(
      "GET",
      `${s(orgId, storeId)}/message-templates`,
    ),
  saveTemplate: (
    orgId: string,
    storeId: string,
    t: { event: string; channel: string; subject: string | null; body: string },
  ) =>
    request<TemplateRow>("PUT", `${s(orgId, storeId)}/message-templates/${t.event}/${t.channel}`, {
      body: { subject: t.subject, body: t.body },
    }),
  resetTemplate: (orgId: string, storeId: string, event: string, channel: string) =>
    request<TemplateRow>("DELETE", `${s(orgId, storeId)}/message-templates/${event}/${channel}`),
  preview: (
    orgId: string,
    storeId: string,
    t: { event: string; channel: string; subject?: string | null; body: string },
  ) =>
    request<TemplatePreview>(
      "POST",
      `${s(orgId, storeId)}/message-templates/${t.event}/${t.channel}/preview`,
      { body: { body: t.body, ...(t.channel === "email" ? { subject: t.subject ?? null } : {}) } },
    ),
  senders: (orgId: string) =>
    request<{ data: SenderIdentity[] }>("GET", `${o(orgId)}/sender-identities`),
  addSender: (orgId: string, body: { kind: SenderIdentity["kind"]; value: string }) =>
    request<SenderIdentity>("POST", `${o(orgId)}/sender-identities`, { body, idempotent: true }),
  removeSender: (orgId: string, id: string) =>
    request<void>("DELETE", `${o(orgId)}/sender-identities/${encodeURIComponent(id)}`),
  verifySender: (orgId: string, id: string) =>
    request<unknown>("POST", `${o(orgId)}/sender-identities/${encodeURIComponent(id)}/verify`, {
      body: {},
    }),
  aliases: (orgId: string, storeId: string) =>
    request<{ data: MailAlias[] }>("GET", `${s(orgId, storeId)}/mail-aliases`),
  addAlias: (orgId: string, storeId: string, body: { alias: string; forward_to: string }) =>
    request<MailAlias>("POST", `${s(orgId, storeId)}/mail-aliases`, { body, idempotent: true }),
  removeAlias: (orgId: string, storeId: string, id: string) =>
    request<void>("DELETE", `${s(orgId, storeId)}/mail-aliases/${encodeURIComponent(id)}`),
};

export const notifyKeys = {
  all: (orgId: string) => ["notifications", orgId] as const,
  list: (orgId: string, unread: boolean) => ["notifications", orgId, "list", unread] as const,
  unread: (orgId: string) => ["notifications", orgId, "unread"] as const,
  prefs: (orgId: string) => ["notifications", orgId, "prefs"] as const,
  push: (orgId: string) => ["notifications", orgId, "push"] as const,
  templates: (orgId: string, storeId: string) =>
    ["notifications", orgId, "templates", storeId] as const,
  senders: (orgId: string) => ["notifications", orgId, "senders"] as const,
  aliases: (orgId: string, storeId: string) =>
    ["notifications", orgId, "aliases", storeId] as const,
};

/** Polling fallback: refreshes every `poll` ms (0 = only on demand, when live updates work). */
export function useUnreadCount(orgId: string, pollMs: number) {
  return useQuery({
    queryKey: notifyKeys.unread(orgId),
    queryFn: () => notificationsApi.unreadCount(orgId),
    refetchInterval: pollMs || false,
  });
}

export function useNotificationList(orgId: string, unread: boolean, pollMs: number) {
  return useQuery({
    queryKey: notifyKeys.list(orgId, unread),
    queryFn: () => notificationsApi.list(orgId, { unread }),
    refetchInterval: pollMs || false,
  });
}
